import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import useDocumentMeta from '../hooks/useDocumentMeta';
import { 
  Heart, 
  Sparkles, 
  CheckCircle2, 
  ArrowRight, 
  Phone, 
  Mail, 
  Users, 
  Trees, 
  ShieldCheck, 
  Sun, 
  Clock, 
  Compass,
  FileCheck,
  HelpCircle,
  ChevronDown
} from 'lucide-react';

export default function Aktivitetstilbud() {
  const [openFaqIndex, setOpenFaqIndex] = React.useState(null);

  useDocumentMeta(
    'Aktivitetstilbud i Brørup | Meningsfuld hverdag med dyr | Ring nu',
    'Oplev et aktivitetstilbud i Brørup med beskyttet beskæftigelse, dyr og natur. Få trygge rammer, tæt støtte og en hverdag med mening hos LIVSLYST.',
    ''
  );

  const faqSchema = {
    "@type": "FAQPage",
    "@context": "https://schema.org",
    "mainEntity": [
      {
        "name": "Hvad er et aktivitetstilbud med beskyttet beskæftigelse?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Hos LIVSLYST er et aktivitetstilbud en del af et beskæftigelsestilbud efter servicelovens § 103. Hverdagen består af tilpassede og meningsfulde arbejdsopgaver, fællesskab og tydelig struktur, så borgeren kan tage ansvar og udvikle færdigheder i eget tempo.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvem kan få et beskæftigelsestilbud efter servicelovens § 103?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "LIVSLYSTs beskæftigelsestilbud henvender sig til voksne under folkepensionsalderen med nedsat fysisk eller psykisk funktionsevne eller særlige sociale udfordringer. Det kan blandt andet være borgere med kognitive vanskeligheder, autisme eller erhvervet hjerneskade, som ikke kan fastholde ordinær beskæftigelse på almindelige vilkår.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvordan bliver man visiteret til beskyttet beskæftigelse?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Det er borgerens bopælskommune, der visiterer til beskyttet beskæftigelse efter servicelovens § 103. Ved en mulig opstart hos LIVSLYST indgår dialog med kommunen og sagsbehandleren, så målgruppe, støttebehov og match kan afklares.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvilke opgaver kan indgå i et aktivitetstilbud med dyr og natur?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Hos LIVSLYST kan opgaverne omfatte dyrepleje, fodring, havearbejde, grøn vedligeholdelse, udendørsopgaver og kreative værkstedsaktiviteter. Opgaverne med dyr og natur tilpasses borgerens interesser, funktionsniveau, motivation og dagsform.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvordan tilpasses støtte og arbejdsopgaver i et beskæftigelsestilbud?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "LIVSLYST opdeler opgaverne i overskuelige trin og justerer løbende krav og støtte efter den enkelte borgers ressourcer og behov. Der kan blandt andet anvendes faste rutiner, tydelig kommunikation, visuel støtte, realistiske mål og en Low Arousal-tilgang.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvad er fordelene ved et lille privat beskæftigelsestilbud?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "LIVSLYST er et privat beskæftigelsestilbud med plads til seks borgere. Den lille ramme giver mulighed for genkendelige relationer, tæt individuelt nærvær, ro og arbejdsopgaver, der kan tilpasses den enkelte. Der lægges samtidig vægt på medbestemmelse og tilhørsforhold til arbejdsfællesskabet.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvordan kan dyr og natur styrke trivsel og udvikling?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Hos LIVSLYST bruges arbejdet med dyr og natur til at skabe konkrete og meningsfulde opgaver. Ansvar for dyr kan understøtte omsorg, struktur og ansvarsfølelse, mens naturaktiviteter kan give bevægelse, variation og overskuelige mål i rolige omgivelser.",
          "@type": "Answer"
        }
      },
      {
        "name": "Kan man besøge et aktivitetstilbud før opstart?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "LIVSLYST tilbyder mulighed for en uforpligtende rundvisning, hvor borgeren og relevante pårørende eller fagpersoner kan opleve rammerne og høre om hverdagen. Man kan også kontakte LIVSLYST om visitation, støttebehov og aktuelle pladser.",
          "@type": "Answer"
        }
      }
    ]
  };

  const faqItems = [
    {
      question: "Hvad er et aktivitetstilbud med beskyttet beskæftigelse?",
      answer: "Hos LIVSLYST er et aktivitetstilbud en del af et beskæftigelsestilbud efter servicelovens § 103. Hverdagen består af tilpassede og meningsfulde arbejdsopgaver, fællesskab og tydelig struktur, så borgeren kan tage ansvar og udvikle færdigheder i eget tempo."
    },
    {
      question: "Hvem kan få et beskæftigelsestilbud efter servicelovens § 103?",
      answer: "LIVSLYSTs beskæftigelsestilbud henvender sig til voksne under folkepensionsalderen med nedsat fysisk eller psykisk funktionsevne eller særlige sociale udfordringer. Det kan blandt andet være borgere med kognitive vanskeligheder, autisme eller erhvervet hjerneskade, som ikke kan fastholde ordinær beskæftigelse på almindelige vilkår."
    },
    {
      question: "Hvordan bliver man visiteret til beskyttet beskæftigelse?",
      answer: "Det er borgerens bopælskommune, der visiterer til beskyttet beskæftigelse efter servicelovens § 103. Ved en mulig opstart hos LIVSLYST indgår dialog med kommunen og sagsbehandleren, så målgruppe, støttebehov og match kan afklares."
    },
    {
      question: "Hvilke opgaver kan indgå i et aktivitetstilbud med dyr og natur?",
      answer: "Hos LIVSLYST kan opgaverne omfatte dyrepleje, fodring, havearbejde, grøn vedligeholdelse, udendørsopgaver og kreative værkstedsaktiviteter. Opgaverne med dyr og natur tilpasses borgerens interesser, funktionsniveau, motivation og dagsform."
    },
    {
      question: "Hvordan tilpasses støtte og arbejdsopgaver i et beskæftigelsestilbud?",
      answer: "LIVSLYST opdeler opgaverne i overskuelige trin og justerer løbende krav og støtte efter den enkelte borgers ressourcer og behov. Der kan blandt andet anvendes faste rutiner, tydelig kommunikation, visuel støtte, realistiske mål og en Low Arousal-tilgang."
    },
    {
      question: "Hvad er fordelene ved et lille privat beskæftigelsestilbud?",
      answer: "LIVSLYST er et privat beskæftigelsestilbud med plads til seks borgere. Den lille ramme giver mulighed for genkendelige relationer, tæt individuelt nærvær, ro og arbejdsopgaver, der kan tilpasses den enkelte. Der lægges samtidig vægt på medbestemmelse og tilhørsforhold til arbejdsfællesskabet."
    },
    {
      question: "Hvordan kan dyr og natur styrke trivsel og udvikling?",
      answer: "Hos LIVSLYST bruges arbejdet med dyr og natur til at skabe konkrete og meningsfulde opgaver. Ansvar for dyr kan understøtte omsorg, struktur og ansvarsfølelse, mens naturaktiviteter kan give bevægelse, variation og overskuelige mål i rolige omgivelser."
    },
    {
      question: "Kan man besøge et aktivitetstilbud før opstart?",
      answer: "LIVSLYST tilbyder mulighed for en uforpligtende rundvisning, hvor borgeren og relevante pårørende eller fagpersoner kan opleve rammerne og høre om hverdagen. Man kan også kontakte LIVSLYST om visitation, støttebehov og aktuelle pladser."
    }
  ];

  const activityHighlights = [
    {
      title: 'Dyrepleje',
      desc: 'Dyrepleje med omsorg, fodring og genkendelige rutiner',
      icon: Heart,
    },
    {
      title: 'Havearbejde',
      desc: 'Havearbejde og praktiske opgaver i de grønne omgivelser',
      icon: Trees,
    },
    {
      title: 'Kreative værksteder',
      desc: 'Kreative værkstedsopgaver med mulighed for ro og fordybelse',
      icon: Sparkles,
    },
    {
      title: 'Fællesskab & trivsel',
      desc: 'Fælles pauser og aktiviteter, der styrker relationer og tilhørsforhold',
      icon: Users,
    },
  ];

  const targetCriteria = [
    'Borgeren har behov for beskyttede og pædagogisk støttede arbejdsrammer',
    'Ordinær beskæftigelse kan ikke fastholdes på almindelige vilkår',
    'Der er brug for struktur, genkendelighed og individuelt tilpassede krav',
    'Bopælskommunen har bevilget beskyttet beskæftigelse efter § 103',
  ];

  return (
    <div className="bg-stone-50 text-stone-800 antialiased overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-stone-100 py-16 md:py-24 border-b border-stone-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 space-y-6"
            >
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100/80 border border-emerald-300 text-emerald-800 text-xs md:text-sm font-semibold tracking-wide shadow-sm">
                <ShieldCheck className="w-4 h-4 text-emerald-700" />
                <span>Servicelovens § 103 · Brørup</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 leading-[1.18]">
                Aktivitetstilbud med beskyttet beskæftigelse i trygge rammer
              </h1>

              <p className="text-lg md:text-xl text-stone-600 leading-relaxed max-w-2xl">
                Hos <Link to="/" className="font-semibold text-emerald-800 hover:text-emerald-900 transition-colors">LIVSLYST</Link> i Brørup får voksne et aktivitetstilbud, hvor praktiske opgaver, fællesskab og tydelig struktur går hånd i hånd. Vores beskyttede beskæftigelse tilrettelægges efter den enkelte borgers ressourcer, interesser og behov for støtte, så hverdagen bliver tryg og meningsfuld.
              </p>

              <div className="pt-2 flex flex-wrap gap-4 items-center">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-emerald-700 text-white font-medium hover:bg-emerald-800 transition-all shadow-sm hover:shadow group"
                >
                  <span>Kontakt os for rundvisning</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="tel:51504654"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white border border-stone-300 text-stone-700 font-medium hover:bg-stone-50 hover:border-stone-400 transition-all shadow-sm"
                >
                  <Phone className="w-4 h-4 text-emerald-600" />
                  <span>51 50 46 54</span>
                </a>
              </div>

              {/* Quick Trust Badges */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-4 border-t border-stone-200/80">
                <div className="flex items-center gap-2 text-xs md:text-sm text-stone-600">
                  <Users className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Maks. 6 borgere</span>
                </div>
                <div className="flex items-center gap-2 text-xs md:text-sm text-stone-600">
                  <Trees className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Dyr & naturmiljø</span>
                </div>
                <div className="flex items-center gap-2 text-xs md:text-sm text-stone-600 col-span-2 sm:col-span-1">
                  <Compass className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Individuel tilpasning</span>
                </div>
              </div>
            </motion.div>

            {/* Hero Image / Authentic photo */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative mx-auto max-w-md lg:max-w-none">
                <div className="absolute -inset-2 bg-gradient-to-tr from-emerald-600 to-amber-600 rounded-3xl opacity-15 blur-lg transform -rotate-1"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-xl border border-stone-200 bg-white">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/86680fd8-15ed-4b67-8bd3-5c4ce8ebdb0a.jpeg"
                    alt="LIVSLYST ApS - Dyr og naturnære rammer i Brørup"
                    className="w-full h-80 sm:h-96 object-cover"
                  />
                  <div className="p-4 bg-stone-900/90 text-white backdrop-blur-sm">
                    <p className="text-xs font-medium text-emerald-300 uppercase tracking-wider">LIVSLYST ApS · Brørup</p>
                    <p className="text-sm font-semibold text-stone-100">Et overskueligt aktivitetstilbud med nærvær, dyr og faste rutiner</p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Sektion: Når aktivitetstilbud bliver meningsfuld beskyttet beskæftigelse */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">§ 103 Serviceloven</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mt-2 mb-6">
              Når aktivitetstilbud bliver meningsfuld beskyttet beskæftigelse
            </h2>
            <p className="text-base sm:text-lg text-stone-600 leading-relaxed mb-5">
              Et aktivitetstilbud hos os er en del af et beskæftigelsestilbud efter <Link to="/paragraf-103" className="font-semibold text-inherit hover:text-emerald-800 transition-colors">servicelovens § 103</Link>. Det betyder, at aktiviteterne har karakter af tilpassede arbejdsopgaver og ikke blot tidsfordriv. Borgeren får mulighed for at tage ansvar, bidrage og udvikle færdigheder i et tempo, der giver mening.
            </p>
            <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
              Vi planlægger opgaverne med blik for funktionsniveau, dagsform og motivation. Nogle trives med faste rutiner omkring dyrene, mens andre foretrækker havearbejde, grøn vedligeholdelse eller kreative opgaver. Krav og støtte justeres løbende, så deltagelsen er realistisk og overskuelig.
            </p>
          </div>

          {/* 4 Aktivitetsområder Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {activityHighlights.map((item, idx) => {
              const IconComp = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1, duration: 0.4 }}
                  className="bg-stone-50 rounded-2xl p-6 border border-stone-200 hover:border-emerald-300 hover:shadow-md transition-all flex flex-col justify-between group"
                >
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center mb-4 group-hover:bg-emerald-700 group-hover:text-white transition-colors">
                      <IconComp className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-bold text-stone-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-stone-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sektion: Hvad kan dyr og natur bidrage med i hverdagen? */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Trivsel i hverdagen</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mt-2 mb-5">
              Hvad kan dyr og natur bidrage med i hverdagen?
            </h2>
            <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
              Dyr og natur er en integreret del af vores miljø for arbejde og læring i Brørup. Opgaverne er konkrete, og deres formål er let at få øje på. Når et dyr skal passes, eller et udeområde skal plejes, kan borgeren opleve, at indsatsen har betydning for hverdagen og for fællesskabet.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-stretch">
            
            {/* Feature 1: Ansvar for dyr */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden flex flex-col"
            >
              <div className="relative h-64 overflow-hidden bg-stone-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/52713da9-b8ad-4644-95bb-94e117047ffb.jpeg"
                  alt="Dyrepleje og nærvær hos LIVSLYST ApS"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-stone-900/80 text-white text-xs px-3 py-1.5 rounded-lg backdrop-blur-sm flex items-center gap-1.5">
                  <Heart className="w-3.5 h-3.5 text-rose-400" />
                  <span>Dyrepleje & Omsorg</span>
                </div>
              </div>

              <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-stone-900 mb-3">
                    Ansvar for dyr med plads til individuel støtte
                  </h3>
                  <p className="text-stone-600 leading-relaxed text-base">
                    Arbejdet med dyr kan understøtte omsorg, ansvarsfølelse og struktur. Den enkelte får støtte til at lære opgaverne og udføre dem på en tryg måde. Målet er ikke, at alle skal kunne det samme, men at hver borger får passende opgaver og mulighed for succesoplevelser uden unødigt pres.
                  </p>
                </div>
                <div className="pt-4 border-t border-stone-100 flex items-center gap-2 text-xs font-semibold text-emerald-800">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Faste, trygge rutiner tilpasset dagsformen</span>
                </div>
              </div>
            </motion.div>

            {/* Feature 2: Naturaktiviteter */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden flex flex-col"
            >
              <div className="relative h-64 overflow-hidden bg-stone-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/b1189328-5fcd-4fd6-9df1-158dddb25196.jpeg"
                  alt="Naturaktiviteter og havearbejde hos LIVSLYST ApS"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-stone-900/80 text-white text-xs px-3 py-1.5 rounded-lg backdrop-blur-sm flex items-center gap-1.5">
                  <Trees className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Natur & Udeområder</span>
                </div>
              </div>

              <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-stone-900 mb-3">
                    Naturaktiviteter med bevægelse og overskuelige mål
                  </h3>
                  <p className="text-stone-600 leading-relaxed text-base">
                    Udendørsopgaver kan skabe variation og give mulighed for at bruge kroppen i rolige omgivelser. Vi opdeler opgaverne i overskuelige trin og bruger tydelig kommunikation, faste rutiner og visuel støtte efter behov. Det kan gøre det lettere at bevare fokus og være aktiv i fællesskabet.
                  </p>
                </div>
                <div className="pt-4 border-t border-stone-100 flex items-center gap-2 text-xs font-semibold text-emerald-800">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Visuel støtte, trin-for-trin opgaver og ro</span>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Sektion: Hvem passer vores beskæftigelsestilbud til? */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <div>
                <span className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Målgruppe & Visitation</span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mt-2">
                  Hvem passer vores beskæftigelsestilbud til?
                </h2>
              </div>

              <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
                Vores beskæftigelsestilbud henvender sig til voksne under folkepensionsalderen, som har nedsat fysisk eller psykisk funktionsevne eller særlige sociale udfordringer. Det kan blandt andet være borgere med kognitive vanskeligheder, autisme eller erhvervet hjerneskade.
              </p>

              <div className="space-y-3 pt-2">
                {targetCriteria.map((criterion, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-stone-50 p-3.5 rounded-xl border border-stone-200/80">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-stone-700 font-medium leading-snug">
                      {criterion}
                    </span>
                  </div>
                ))}
              </div>

              <p className="text-stone-600 leading-relaxed text-sm sm:text-base pt-2">
                Det er bopælskommunen, der visiterer til beskyttet beskæftigelse efter servicelovens § 103. Derfor indgår dialog med kommune og sagsbehandler som en naturlig del af opstarten. Sammen ser vi på målgruppe, støttebehov og match, så borgeren får en afklaret og tryg begyndelse hos LIVSLYST.
              </p>

              <div className="pt-2">
                <Link
                  to="/maalgruppe"
                  className="inline-flex items-center gap-2 text-emerald-700 font-semibold hover:text-emerald-800 hover:underline text-sm sm:text-base"
                >
                  <span>Læs mere om vores målgruppe</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="bg-stone-50 rounded-2xl p-6 sm:p-8 border border-stone-200 shadow-sm relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-emerald-700 text-white flex items-center justify-center shrink-0">
                    <FileCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-stone-900">Visitationsproces i korte træk</h3>
                    <p className="text-xs text-stone-500">I samarbejde med din bopælskommune</p>
                  </div>
                </div>

                <ol className="space-y-4">
                  <li className="flex items-start gap-4">
                    <span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                      1
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-stone-900">Kontakt sagsbehandler</p>
                      <p className="text-xs text-stone-600 mt-0.5">Tal med kommunen om bevilling til beskyttet beskæftigelse efter servicelovens § 103.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                      2
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-stone-900">Uforpligtende dialog & rundvisning</p>
                      <p className="text-xs text-stone-600 mt-0.5">Besøg LIVSLYST i Brørup for at se rammerne, møde os og høre om hverdagen.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                      3
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-stone-900">Match & Tryg opstart</p>
                      <p className="text-xs text-stone-600 mt-0.5">Vi tilrettelægger arbejdsopgaver, støtte og forudsigelige rammer fra første dag.</p>
                    </div>
                  </li>
                </ol>

                <div className="mt-6 pt-6 border-t border-stone-200 bg-emerald-50/60 -mx-6 -mb-6 p-6 rounded-b-2xl">
                  <p className="text-xs text-emerald-900 font-medium">
                    Har du eller din sagsbehandler spørgsmål? Ring gerne direkte på <a href="tel:51504654" className="font-bold underline text-emerald-800 whitespace-nowrap">51 50 46 54</a>.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion: Hvorfor vælge et lille privat beskæftigelsestilbud? */}
      <section className="py-16 md:py-24 bg-stone-100 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-lg border border-stone-200 bg-white">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/c5f66cf8-78de-45ab-990a-d94928372a1d.jpeg"
                  alt="Fællesskab og hygge hos LIVSLYST ApS"
                  className="w-full h-80 sm:h-96 object-cover"
                />
                <div className="p-4 bg-stone-900/90 text-white backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-emerald-400">NÆRVÆR & RO</p>
                      <p className="text-sm font-medium text-stone-200">Trygge rammer med maks. 6 borgere</p>
                    </div>
                    <Users className="w-6 h-6 text-emerald-400" />
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6 order-1 lg:order-2">
              <div>
                <span className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Det nære miljø</span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mt-2">
                  Hvorfor vælge et lille privat beskæftigelsestilbud?
                </h2>
              </div>

              <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
                LIVSLYST er et privat beskæftigelsestilbud med plads til seks borgere. Den lille ramme giver mulighed for genkendelige relationer, tæt individuelt nærvær og opgaver, der kan tilpasses den enkelte. Vi lægger vægt på ro, medbestemmelse og oplevelsen af at høre til i et arbejdsfællesskab.
              </p>

              <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
                Vores tilgang er anerkendende og omsorgsfuld. Vi arbejder blandt andet med Low Arousal, tydelig kommunikation og realistiske mål, når det passer til borgerens behov. Udvikling følges gennem individuelle mål, samtaler og løbende evaluering, altid med trivsel som et vigtigt udgangspunkt.
              </p>

              {/* 3 Values Pills */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm">
                  <p className="text-sm font-bold text-stone-900">Low Arousal</p>
                  <p className="text-xs text-stone-500 mt-1">Konfliktnedtrappende og stressreducerende tilgang</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm">
                  <p className="text-sm font-bold text-stone-900">Maks. 6 pladser</p>
                  <p className="text-xs text-stone-500 mt-1">Overskueligt miljø med faste kendte ansigter</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm">
                  <p className="text-sm font-bold text-stone-900">SMART-mål</p>
                  <p className="text-xs text-stone-500 mt-1">Løbende evaluering og individuel udvikling</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion: Ofte stillede spørgsmål (FAQ) - 2 Kolonner */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200 relative overflow-hidden">
        {/* Schema.org FAQPage structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100/80 text-emerald-800 text-xs font-bold uppercase tracking-wider border border-emerald-200/60 shadow-2xs">
              <HelpCircle className="w-3.5 h-3.5" />
              <span>Ofte stillede spørgsmål</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
              Spørgsmål & svar om aktivitetstilbud
            </h2>
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
              Her finder du svar på de mest almindelige spørgsmål om vores aktivitetstilbud med beskyttet beskæftigelse, visitation, rammer og hverdagen med dyr og natur.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 items-start">
            {/* Venstre kolonne */}
            <div className="space-y-3 sm:space-y-4">
              {faqItems.slice(0, Math.ceil(faqItems.length / 2)).map((item, index) => {
                const actualIndex = index;
                const isOpen = openFaqIndex === actualIndex;
                return (
                  <motion.div
                    key={actualIndex}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? 'bg-white border-emerald-300/80 shadow-sm ring-1 ring-emerald-200/50'
                        : 'bg-white border-stone-200 hover:border-stone-300 hover:shadow-xs'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : actualIndex)}
                      className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left gap-4 cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700"
                      aria-expanded={isOpen}
                    >
                      <span className="text-base sm:text-lg font-semibold text-stone-900 leading-snug">
                        {item.question}
                      </span>
                      <span
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                          isOpen
                            ? 'bg-emerald-800 text-white rotate-180'
                            : 'bg-stone-100 text-stone-600'
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>

                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className="px-5 sm:px-6 pb-5 pt-1 text-stone-600 text-sm sm:text-base leading-relaxed border-t border-stone-100"
                      >
                        <p>{item.answer}</p>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Højre kolonne */}
            <div className="space-y-3 sm:space-y-4">
              {faqItems.slice(Math.ceil(faqItems.length / 2)).map((item, idx) => {
                const actualIndex = idx + Math.ceil(faqItems.length / 2);
                const isOpen = openFaqIndex === actualIndex;
                return (
                  <motion.div
                    key={actualIndex}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: (idx + 4) * 0.05 }}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? 'bg-white border-emerald-300/80 shadow-sm ring-1 ring-emerald-200/50'
                        : 'bg-white border-stone-200 hover:border-stone-300 hover:shadow-xs'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : actualIndex)}
                      className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left gap-4 cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700"
                      aria-expanded={isOpen}
                    >
                      <span className="text-base sm:text-lg font-semibold text-stone-900 leading-snug">
                        {item.question}
                      </span>
                      <span
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                          isOpen
                            ? 'bg-emerald-800 text-white rotate-180'
                            : 'bg-stone-100 text-stone-600'
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>

                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className="px-5 sm:px-6 pb-5 pt-1 text-stone-600 text-sm sm:text-base leading-relaxed border-t border-stone-100"
                      >
                        <p>{item.answer}</p>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Under-FAQ kontaktboks */}
          <div className="mt-12 text-center bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 shadow-2xs max-w-2xl mx-auto">
            <h3 className="text-lg font-bold text-stone-900">Har du et spørgsmål, som ikke er nævnt her?</h3>
            <p className="text-sm text-stone-600 mt-1 max-w-md mx-auto">
              Vi står altid klar til en uforpligtende snak om målgruppe, visitation og mulighederne hos LIVSLYST.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-800 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-2xs"
              >
                <span>Kontakt os i dag</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:51504654"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-stone-100 text-stone-800 text-sm font-semibold hover:bg-stone-200 transition-colors"
              >
                <Phone className="w-4 h-4 text-emerald-700" />
                <span>51 50 46 54</span>
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* Sektion: Er LIVSLYST det rette aktivitetstilbud? (CTA) */}
      <section className="py-16 md:py-24 bg-stone-900 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-950 border border-emerald-700 text-emerald-300 text-xs font-semibold uppercase tracking-wider">
              Næste skridt
            </span>

            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white leading-tight">
              Er LIVSLYST det rette aktivitetstilbud?
            </h2>

            <p className="text-lg text-stone-300 leading-relaxed">
              Det bedste match begynder med en åben dialog om borgerens ønsker, ressourcer og behov for støtte. Du er velkommen til at kontakte os om visitation, aktuelle pladser eller en uforpligtende rundvisning i Brørup. Her kan du opleve rammerne og høre mere om hverdagen med dyr og natur.
            </p>

            <div className="pt-4 flex flex-wrap justify-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-500 transition-all shadow-md group"
              >
                <span>Book en rundvisning</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="tel:51504654"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-stone-800 text-stone-200 border border-stone-700 font-semibold hover:bg-stone-700 hover:text-white transition-all whitespace-nowrap"
              >
                <Phone className="w-4 h-4 text-emerald-400" />
                <span>Ring på 51 50 46 54</span>
              </a>
            </div>

            <div className="pt-6 flex flex-wrap justify-center items-center gap-6 text-xs text-stone-400">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                Uforpligtende samtale
              </span>
              <span className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-emerald-400" />
                For borgere, pårørende & sagsbehandlere
              </span>
              <span className="flex items-center gap-1.5">
                <Trees className="w-4 h-4 text-emerald-400" />
                Surhavevej 14 A, 6650 Brørup
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
